import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../utils/platform';
import { getPendingPayments, getAllPayments, approvePayment, rejectPayment, getChurchProfile, getCRDeletionRequests, decideCRDeletion } from '../../services/api';
import { FiCheckCircle, FiXCircle, FiImage, FiX, FiPrinter } from 'react-icons/fi';
import Toast from '../../components/Toast';
import { printCashReceipt } from '../../utils/printReceipt';
import { useAuth } from '../../context/AuthContext';
import AuthImg from '../../components/AuthImg';
import useAppRefresh from '../../hooks/useAppRefresh';

const statusPill = { pending: 'pill-yellow', approved: 'pill-green', rejected: 'pill-red' };

const PendingApprovals = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [tab, setTab] = useState('pending');
  const [pending, setPending] = useState([]);
  const [all, setAll] = useState([]);
  const [profile, setProfile] = useState({});
  const [toast, setToast] = useState(null);
  const [viewImg, setViewImg] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [saving, setSaving] = useState(false);
  // v1.9.25 — CR deletion requests queue.
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [deletionRejectModal, setDeletionRejectModal] = useState(null);
  const [deletionRejectNote, setDeletionRejectNote] = useState('');

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = () => {
    getPendingPayments().then(r => setPending(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    getAllPayments().then(r => setAll(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    getCRDeletionRequests({ status: 'pending' }).then(r => setDeletionRequests(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  };

  useEffect(() => {
    load();
    getChurchProfile().then(r => setProfile(r.data || {})).catch(() => {});
  }, []);
  useAppRefresh(load);

  const handleApprove = async (req) => {
    if (!window.confirm(t('pendingApprovals.approveConfirm', { name: req.member_name?.trim() }))) return;
    setSaving(true);
    try {
      const res = await approvePayment(req.id);
      showToast(t('pendingApprovals.approvedToast', { cr: res.data.cr_number }));
      printCashReceipt({
        cr_number: res.data.cr_number,
        date: new Date().toISOString().slice(0, 10),
        received_from: req.member_name?.trim() || '',
        spiritual_father_name: req.spiritual_father_name || '',
        hagest_sbket: profile.hagest_sbket || '',
        city: profile.city || '',
        fund_name: req.fund_name || '',
        payment_method: req.payment_method || '',
        amounts: req.amounts || {},
        notes: req.notes || '',
        received_by_name: user?.name || '',
        verify_token: res.data?.verify_token || '',
      }, profile);
      load();
    } catch (e) { showToast(e.response?.data?.error || t('myProfile.failed'), 'error'); }
    finally { setSaving(false); }
  };

  const reprintReceipt = (r) => {
    printCashReceipt({
      cr_number: r.cr_number || (r.cr_id ? `CR-${String(r.cr_id).padStart(4, '0')}` : ''),
      date: r.submitted_at?.slice(0, 10) || new Date().toISOString().slice(0, 10),
      received_from: r.member_name?.trim() || '',
      member_card: r.member_card || '',
      spiritual_father_name: r.spiritual_father_name || '',
      hagest_sbket: r.hagest_sbket || profile.hagest_sbket || '',
      city: r.city || profile.city || '',
      fund_name: r.fund_name || '',
      payment_method: r.payment_method || '',
      amounts: r.amounts || {},
      notes: r.notes || '',
      received_by_name: r.reviewed_by_name || user?.name || '',
      verify_token: r.verify_token || r.cr_verify_token || '',
    }, profile);
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) return;
    setSaving(true);
    try {
      await rejectPayment(rejectModal.id, { rejection_reason: rejectionReason });
      showToast(t('pendingApprovals.rejectedToast'));
      setRejectModal(null);
      setRejectionReason('');
      load();
    } catch (e) { showToast(t('myProfile.failed'), 'error'); }
    finally { setSaving(false); }
  };

  const list = tab === 'pending' ? pending : all;

  const handleDeletionDecision = async (req, decision, note) => {
    setSaving(true);
    try {
      await decideCRDeletion(req.id, decision, note || '');
      showToast(decision === 'approve' ? `${req.cr_number} deleted.` : `${req.cr_number} kept.`);
      setDeletionRejectModal(null); setDeletionRejectNote('');
      load();
    } catch (e) { showToast(e.response?.data?.error || 'Action failed', 'error'); }
    finally { setSaving(false); }
  };
  const confirmApproveDeletion = (req) => {
    if (!window.confirm(`Approve deletion of ${req.cr_number}? The CR will be permanently removed and any subscription periods it settled will revert to unpaid.`)) return;
    handleDeletionDecision(req, 'approve');
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1>{t('nav.pendingApprovals')}</h1>
          <p>{pending.length}</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid #e5e7eb', marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { key: 'pending', label: `${t('subscriptions.due')} (${pending.length})` },
          { key: 'all', label: t('common.all') },
          { key: 'deletions', label: `CR Deletions (${deletionRequests.length})` },
        ].map(tab2 => (
          <button key={tab2.key} onClick={() => setTab(tab2.key)} style={{
            padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: 600, fontSize: 13, color: tab === tab2.key ? '#4f46e5' : '#6b7280',
            borderBottom: tab === tab2.key ? '2px solid #4f46e5' : '2px solid transparent', marginBottom: -2
          }}>{tab2.label}</button>
        ))}
      </div>

      {tab === 'deletions' ? (
        <div className="table-card">
          <table>
            <thead>
              <tr>
                <th>#</th><th>CR</th><th>Date</th><th>Received From</th><th>Fund</th>
                <th>Amount $</th><th>Amount K</th><th>Reason</th><th>Requested by</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {deletionRequests.length === 0 ? (
                <tr><td colSpan={10}><div className="empty-state"><p>No pending CR deletion requests.</p></div></td></tr>
              ) : deletionRequests.map((r, i) => (
                <tr key={r.id} style={{ background: '#fffbeb' }}>
                  <td>{i + 1}</td>
                  <td><strong>{r.cr_number}</strong></td>
                  <td>{r.cr_date}</td>
                  <td>{r.received_from || '—'}</td>
                  <td>{r.fund_name || '—'}</td>
                  <td>{r.amount_usd > 0 ? `$${parseFloat(r.amount_usd).toFixed(2)}` : '—'}</td>
                  <td>{r.amount_kwacha > 0 ? `K${parseFloat(r.amount_kwacha).toFixed(2)}` : '—'}</td>
                  <td style={{ fontStyle: 'italic', color: '#374151', maxWidth: 260 }}>{r.reason}</td>
                  <td style={{ fontSize: 12 }}>
                    <div><strong>{r.requested_by_name}</strong></div>
                    <div style={{ color: '#6b7280', fontFamily: 'Courier New, monospace', fontSize: 11 }}>{r.requester_role}</div>
                    <div style={{ color: '#6b7280', fontSize: 11 }}>{r.requested_at?.slice(0, 16).replace('T', ' ')}</div>
                  </td>
                  <td>
                    {r.can_approve ? (
                      <div className="action-btns">
                        <button className="btn btn-sm btn-success" onClick={() => confirmApproveDeletion(r)} disabled={saving}>
                          <FiCheckCircle /> Approve delete
                        </button>
                        <button className="btn btn-sm btn-danger" onClick={() => { setDeletionRejectModal(r); setDeletionRejectNote(''); }} disabled={saving}>
                          <FiXCircle /> Reject
                        </button>
                      </div>
                    ) : r.mine ? (
                      <span style={{ fontSize: 11, color: '#92400e' }}>⏳ Awaiting opposite role</span>
                    ) : (
                      <span style={{ fontSize: 11, color: '#6b7280' }}>Same role — cannot review</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>#</th><th>{t('common.date')}</th><th>{t('nav.members')}</th><th>{t('members.spiritualFather')}</th>
              <th>{t('finance.fund')}</th>{(profile.currencies && profile.currencies.length ? profile.currencies : ['$','K']).map(sym => <th key={sym}>Amount {sym}</th>)}<th>{t('finance.paymentMethod')}</th>
              <th>{t('finance.diocese')}</th><th>{t('finance.city')}</th><th>{t('approve.viewScreenshot')}</th><th>{t('common.status')}</th>
              {tab === 'pending' && <th>{t('common.actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={11 + (profile.currencies && profile.currencies.length ? profile.currencies.length : 2)}><div className="empty-state"><p>{t('finance.noRecords')}</p></div></td></tr>
            ) : list.map((r, i) => (
              <tr key={r.id} style={{ background: r.status === 'pending' ? '#fffbeb' : 'transparent' }}>
                <td data-label="#">{i + 1}</td>
                <td data-label={t('common.date')} style={{ fontSize: 12 }}>{r.submitted_at?.slice(0, 16)}</td>
                <td data-label={t('nav.members')}><strong>{r.member_name?.trim() || '—'}</strong></td>
                <td data-label={t('members.spiritualFather')}>{r.spiritual_father_name || '—'}</td>
                <td data-label={t('finance.fund')}>{r.fund_name || '—'}</td>
                {(profile.currencies && profile.currencies.length ? profile.currencies : ['$','K']).map(sym => {
                  const v = parseFloat((r.amounts || {})[sym] || 0);
                  return (
                    <td key={sym} data-label={`Amount ${sym}`} style={{ color: v > 0 ? '#16a34a' : '#9ca3af', fontWeight: 600 }}>
                      {v > 0 ? `${sym}${v.toFixed(2)}` : '—'}
                    </td>
                  );
                })}
                <td data-label={t('finance.paymentMethod')} style={{ textTransform: 'capitalize' }}>{r.payment_method}</td>
                <td data-label={t('finance.diocese')}>{r.hagest_sbket || '—'}</td>
                <td data-label={t('finance.city')}>{r.city || '—'}</td>
                <td data-label={t('approve.viewScreenshot')}>
                  {r.screenshot
                    ? <button className="btn btn-sm btn-secondary" onClick={() => setViewImg(r.screenshot)}>
                        <FiImage /> {t('pendingApprovals.viewBtn')}
                      </button>
                    : '—'}
                </td>
                <td data-label={t('common.status')}>
                  <span className={`pill ${statusPill[r.status]}`}>{t(`paymentStatus.${r.status}`, r.status)}</span>
                  {r.status === 'rejected' && r.rejection_reason && (
                    <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>{r.rejection_reason}</div>
                  )}
                  {r.status === 'approved' && r.cr_id && (
                    <div style={{ fontSize: 11, color: '#16a34a', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>{t('pendingApprovals.crGenerated')}</span>
                      {!isMobile && <button className="btn-icon" title={t('pendingApprovals.viewBtn')} onClick={() => reprintReceipt(r)} style={{ padding: 2 }}><FiPrinter /></button>}
                    </div>
                  )}
                </td>
                {tab === 'pending' && (
                  <td data-label={t('common.actions')}>
                    <div className="action-btns">
                      <button className="btn btn-sm btn-success" onClick={() => handleApprove(r)} disabled={saving}>
                        <FiCheckCircle /> {t('approve.approveBtn')}
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => { setRejectModal(r); setRejectionReason(''); }}>
                        <FiXCircle /> {t('approve.rejectBtn')}
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* Deletion reject modal */}
      {deletionRejectModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <div><h3>Reject deletion of {deletionRejectModal.cr_number}?</h3><p>The CR stays. Please explain the rejection.</p></div>
              <button className="btn-icon" onClick={() => setDeletionRejectModal(null)}><FiX /></button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                Requested by <b>{deletionRejectModal.requested_by_name}</b> — reason: <i>"{deletionRejectModal.reason}"</i>
              </div>
              <div className="form-group">
                <label>Rejection note <span className="required">*</span></label>
                <textarea value={deletionRejectNote} onChange={e => setDeletionRejectNote(e.target.value)} rows={3} autoFocus placeholder="e.g. Amount is correct; keep the CR." />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeletionRejectModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => handleDeletionDecision(deletionRejectModal, 'reject', deletionRejectNote)} disabled={saving || !deletionRejectNote.trim()}>
                {saving ? 'Saving…' : 'Confirm reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Screenshot modal */}
      {viewImg && (
        <div className="modal-overlay" onClick={() => setViewImg(null)} style={{ alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <button
            onClick={() => setViewImg(null)}
            aria-label="Close"
            style={{ position: 'fixed', top: 14, right: 14, background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '50%', width: 40, height: 40, fontSize: 22, cursor: 'pointer', zIndex: 10 }}
          >✕</button>
          <div style={{ maxWidth: 600, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }} onClick={e => e.stopPropagation()}>
            <AuthImg filename={viewImg} alt="Screenshot" style={{ maxWidth: '100%', maxHeight: '78vh', objectFit: 'contain', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }} fallback={<div style={{ color: '#fff', padding: 24 }}>Loading…</div>} />
            <button className="btn btn-secondary" onClick={() => setViewImg(null)}>{t('common.close')}</button>
          </div>
        </div>
      )}

      {/* Reject modal */}
      {rejectModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <div><h3>{t('approve.rejectBtn')}</h3><p>{rejectModal.member_name?.trim()}</p></div>
              <button className="btn-icon" onClick={() => setRejectModal(null)}><FiX /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>{t('approve.rejectionReason')} <span className="required">*</span></label>
                <textarea value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} rows={3} autoFocus />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setRejectModal(null)}>{t('common.cancel')}</button>
              <button className="btn btn-danger" onClick={handleReject} disabled={saving || !rejectionReason.trim()}>
                {saving ? t('common.saving') : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default PendingApprovals;
